/**
 * DOCUMENTATION:
 * Component: CssDashboard
 * Purpose: Central hub for the CSS, SCSS, and Flexbox curriculum.
 * * Note for Junior Developers:
 * - CSS is often underestimated. Writing code that "looks right" is easy; writing CSS that
 * scales gracefully across a massive application without side effects is true engineering.
 * - This dashboard uses `useMemo` for the lesson array and search filtering to prevent
 * expensive recalculations during renders.
 */

import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Stack,
    styled,
    useTheme,
    useMediaQuery,
    TextField,
    InputAdornment,
    Fab,
    Zoom,
    LinearProgress,
} from "@mui/material";

/* Icons */
import CodeIcon from "@mui/icons-material/Code";
import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

/* Custom Components */
// Adjust these relative paths based on your actual project directory structure
import RadioOption from "../../../components/RadioOption";
import Typography from "../../../components/Typography";
import LanguageTabs from "../../../components/LanguageTabs";
import LectureTimer from "../../../components/LectureTimer";

/* Premium Option‑A Background */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.90), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingTop: theme.spacing(15),
    paddingBottom: theme.spacing(10),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(12),
        paddingBottom: theme.spacing(5),
    },
}));

const GlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    height: "100%",
}));

const TopicBox = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: "rgba(255,255,255,0.05)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "10px",
    padding: theme.spacing(1.5, 2),
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "rgba(218, 165, 32, 0.1)",
        borderColor: "goldenrod",
    },
}));

const ViewButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 700,
    borderRadius: "999px",
    padding: "6px 20px",
    textTransform: "none",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

export default function CssDashboard() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* State Management */
    const [searchQuery, setSearchQuery] = useState("");
    const [showScroll, setShowScroll] = useState(false);
    const [completedCount, setCompletedCount] = useState(0);

    /* ---------------- CSS/SCSS CURRICULUM LESSONS ---------------- */
    const lessons = [
        { title: "CSS Fundamentals (Box Model, Specificity & Units)", link: "/css-fundamentals" },
        { title: "Modern Layouts (Flexbox Deep Dive)", link: "/css-flexbox" },
        { title: "Advanced Layouts (CSS Grid)", link: "/css-grid" },
        { title: "SCSS Preprocessor (Variables, Nesting, Mixins)", link: "/css-scss-preprocessor" },
        { title: "Animations & Transitions", link: "/css-animations" },
        { title: "Responsive Design & Architecture (BEM)", link: "/css-responsive-architecture" },
        { title: "CSS & UI Practical Project", link: "/css-practical-project" },
        { title: "CSS & SCSS Certification Quiz", link: "/css-quiz" },
    ];

    /* Logic for Filtering & Scroll */
    const filteredLessons = useMemo(() => {
        return lessons.filter(lesson =>
            lesson.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [searchQuery]);

    // Calculate progress percentage dynamically
    const progressPercentage = (completedCount / lessons.length) * 100;

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    return (
        <PageWrapper>
            {/* ---------------- GOLDEN BANNER ---------------- */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 16, px: 2 }}>
                <Typography
                    variant={isMobile ? "h6" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        px: { xs: 3, md: 6 },
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    CSS, SCSS & FLEXBOX
                </Typography>
            </Box>

            {/* ---------------- LANGUAGE TABS ---------------- */}
            <LanguageTabs active="CSS" />

            {/* ---------------- MAIN CONTENT ---------------- */}
            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 }, mt: 2 }}>

                {/* LEFT COLUMN: Video & Dashboard */}
                <Grid item xs={12} md={5}>
                    <GlassCard>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, textAlign: "center", mb: 2, color: "goldenrod" }}
                        >
                            SELF STUDY DASHBOARD
                        </Typography>

                        <Card
                            sx={{
                                width: "100%",
                                backgroundColor: "#f5f5f5",
                                textAlign: "center",
                                padding: { xs: "10px", md: "20px" },
                                borderRadius: "12px",
                                mt: 2,
                            }}
                        >
                            {/* Embedded CSS Crash Course Video */}
                            <CardMedia
                                component="iframe"
                                height={isMobile ? "200" : "260"}
                                src="https://www.youtube.com/embed/1Rs2ND1ryYc" // Generic CSS Crash Course Placeholder
                                title="Free CSS Tutorial"
                                allowFullScreen
                                sx={{ borderRadius: "8px", border: "none", mb: 2 }}
                            />

                            <CardContent sx={{ color: "#100720" }}>
                                <Typography variant="subtitle1" fontWeight={800} gutterBottom>
                                    Master UI Engineering & Styling
                                </Typography>

                                <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
                                    <PersonalVideoIcon fontSize="small" />
                                    <Typography variant="body2" fontWeight={600}>
                                        Mode: Online Self‑paced
                                    </Typography>
                                </Stack>

                                <Link to="/compiler" style={{ textDecoration: "none" }}>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        startIcon={<CodeIcon />}
                                        sx={{
                                            bgcolor: "#100720",
                                            color: "white",
                                            fontWeight: "bold",
                                            py: 1.5,
                                            borderRadius: "8px",
                                            "&:hover": { bgcolor: "#1a0c36" }
                                        }}
                                    >
                                        Hands‑on Styling
                                    </Button>
                                </Link>
                            </CardContent>
                        </Card>

                        {/* ---------------- TIMER ---------------- */}
                        <Box sx={{ mt: 3 }}>
                            <LectureTimer />
                        </Box>
                    </GlassCard>
                </Grid>

                {/* RIGHT COLUMN — LESSONS WITH PROGRESS & SEARCH */}
                <Grid item xs={12} md={7}>
                    <GlassCard>
                        <Typography
                            variant="h6"
                            sx={{ fontWeight: 700, color:"white", textAlign: "center", mb: 3 }}
                        >
                            STYLING CURRICULUM
                        </Typography>

                        {/* Progress Bar Section */}
                        <Box sx={{ mb: 3, px: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="caption" sx={{ color: "goldenrod", fontWeight: "bold" }}>
                                    Overall Progress
                                </Typography>
                                <Typography variant="caption" sx={{ color: "goldenrod", fontWeight: "bold" }}>
                                    {Math.round(progressPercentage)}%
                                </Typography>
                            </Box>
                            <LinearProgress
                                variant="determinate"
                                value={progressPercentage}
                                sx={{
                                    height: 8,
                                    borderRadius: 5,
                                    bgcolor: "rgba(255,255,255,0.1)",
                                    "& .MuiLinearProgress-bar": { bgcolor: "goldenrod" }
                                }}
                            />
                        </Box>

                        {/* Search Bar */}
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Find a topic..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ mb: 3 }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: "goldenrod" }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    color: "white",
                                    bgcolor: "rgba(255,255,255,0.05)",
                                    borderRadius: "8px",
                                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(218, 165, 32, 0.3)" }
                                }
                            }}
                        />

                        {/* Curriculum List */}
                        <Stack spacing={1.5}>
                            {filteredLessons.length > 0 ? (
                                filteredLessons.map((lesson, i) => (
                                    <TopicBox key={i}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexGrow: 1 }}>
                                            <RadioOption />
                                            <Typography variant="body2" sx={{ fontWeight: 500 , color:"white"}}>
                                                {lesson.title}
                                            </Typography>
                                        </Box>
                                        <Link to={lesson.link} style={{ textDecoration: "none" }}>
                                            <ViewButton size="small">View</ViewButton>
                                        </Link>
                                    </TopicBox>
                                ))
                            ) : (
                                <Typography sx={{ textAlign: "center", opacity: 0.5, py: 4 }}>
                                    No topics found matching "{searchQuery}"
                                </Typography>
                            )}
                        </Stack>
                    </GlassCard>
                </Grid>
            </Grid>

            {/* Floating Scroll to Top */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{
                        position: "fixed",
                        bottom: 25,
                        right: 25,
                        bgcolor: "goldenrod",
                        "&:hover": { bgcolor: "#e0b200" }
                    }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
